/**
 * anchorMatch.js — Finds the current line of a gate issue in a Java file after
 * line-shifting edits (Mark TODO, Comment Out Line, Block Comment, AI Apply Fix).
 *
 * WHY THIS EXISTS:
 *   When a user applies a fix (e.g. "Mark TODO" replaces line 100 with a 2-line
 *   comment block), all issues below line 100 shift by +1. The stored
 *   javaLocation.line from the gate report is now stale. This helper searches for
 *   the original failing-line text (issue.snippet) in the current live editor
 *   content to find where that line actually is now.
 *
 * STRATEGY (in order):
 *   1. Exact trimmed match anywhere in the file — scored by context proximity
 *      and closeness to the stated line number (proximity bonus).
 *   2. Fuzzy case-insensitive match — same scoring.
 *   3. Partial leading-content match (first 50 chars, min length 15).
 *   4. Return statedLine unchanged as a safe fallback.
 *
 * WHEN ANCHOR FAILS GRACEFULLY:
 *   If the original line was already replaced by a prior fix (e.g. the line now
 *   reads "// TODO: ..."), the exact match won't fire for that issue. The fallback
 *   returns statedLine, which may be off by a few lines but is still close.
 *   The subsequent auto-refresh re-run will reload the issue list with accurate
 *   line numbers from the fresh gate report.
 */

/**
 * Find the current 1-indexed line number for an issue in potentially-shifted file content.
 *
 * @param {string}   fileContent    Full current text of the Java file.
 * @param {number}   statedLine     issue.javaLocation.line (1-indexed, may be stale).
 * @param {string}   [originalLine] Trimmed source line text (from issue.snippet).
 * @param {string[]} [contextBefore] Lines immediately before originalLine in the report.
 * @param {string[]} [contextAfter]  Lines immediately after originalLine in the report.
 * @returns {number} Best matching 1-indexed line number, or statedLine if no match found.
 */
export function findAnchorLine(fileContent, statedLine, originalLine, contextBefore = [], contextAfter = []) {
  if (!fileContent || !statedLine) return statedLine || 1

  const lines = fileContent.split('\n')
  const totalLines = lines.length

  const search = (originalLine || '').trim()
  // No snippet to search — clamp statedLine to file bounds and return
  if (!search) return Math.min(statedLine, totalLines)

  // Score a 0-indexed candidate line by how well its neighbours match
  // the context arrays captured when the decision was persisted.
  const scoreCandidate = (idx) => {
    let score = 0
    // context_before lines run backwards from idx
    ;[...contextBefore].reverse().forEach((ctx, i) => {
      const ci = idx - 1 - i
      if (ci >= 0 && lines[ci]?.trim() === ctx.trim()) score += 2
    })
    // context_after lines run forward from idx
    contextAfter.forEach((ctx, i) => {
      const ci = idx + 1 + i
      if (ci < totalLines && lines[ci]?.trim() === ctx.trim()) score += 2
    })
    // Proximity bonus: lines close to the originally-reported line are preferred
    // (avoids picking a distant duplicate of the same token pattern).
    score += Math.max(0, 30 - Math.abs(idx + 1 - statedLine)) * 0.05
    return score
  }

  // ── 1. Exact trimmed match ────────────────────────────────────────────────
  const exactMatches = []
  for (let i = 0; i < totalLines; i++) {
    if (lines[i]?.trim() === search) exactMatches.push(i)
  }

  if (exactMatches.length === 1) return exactMatches[0] + 1

  if (exactMatches.length > 1) {
    const best = exactMatches.reduce((a, b) => scoreCandidate(a) >= scoreCandidate(b) ? a : b)
    return best + 1
  }

  // ── 2. Fuzzy case-insensitive match ───────────────────────────────────────
  const searchLower = search.toLowerCase()
  const fuzzyMatches = []
  for (let i = 0; i < totalLines; i++) {
    if (lines[i]?.trim().toLowerCase() === searchLower) fuzzyMatches.push(i)
  }

  if (fuzzyMatches.length > 0) {
    const best = fuzzyMatches.reduce((a, b) => scoreCandidate(a) >= scoreCandidate(b) ? a : b)
    return best + 1
  }

  // ── 3. Partial leading-content match (avoids short/generic false matches) ─
  const searchPartial = search.slice(0, 50).trim()
  if (searchPartial.length >= 15) {
    for (let i = 0; i < totalLines; i++) {
      if (lines[i]?.trim().startsWith(searchPartial)) return i + 1
    }
  }

  // ── 4. Safe fallback — clamped to file bounds ─────────────────────────────
  // Clamp to totalLines so we never scroll Monaco past EOF (shows last line / closing brace).
  return Math.min(statedLine, totalLines)
}
